import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import moment from 'moment-timezone';
import { parseColor } from 'tailwindcss/lib/util/color';
import encryptDecrypt from './encryptDecrypt';
import {
  ALLOWED_LOCATION_RAG_PROMPT_VARIABLES,
  ALLOWED_PROMPT_VARIABLES,
  ALLOWED_REGION_RAG_PROMPT_VARIABLES,
  ALLOWED_VOICE_PROMPT_VARIABLES,
  ROLE,
} from './enum';
import { DATETIME_FORMAT } from './map';
import { IPROMPT, Template, VisitorDetailsType } from './types';
import { isNonClientRoute } from './nonClientRoutes';
import { getLastClientId } from './lastClient';

dayjs.extend(duration);
dayjs.extend(utc);

const downloadFile = (url: string) => {
  if (url) {
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop() || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const downloadCSV = (
  rows: Record<string, any>[],
  headers: { key: string; label: string }[],
  filename: string
) => {
  const escapeCell = (value: unknown) => {
    const str = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const csvContent =
    '﻿' +
    [
      headers.map((header) => escapeCell(header.label)).join(','),
      ...rows.map((row) =>
        headers.map((header) => escapeCell(row[header.key])).join(',')
      ),
    ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const findSessionDuration = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const duration = endDate.getTime() - startDate.getTime();
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const isAgentWidgetOpen = () => {
  const agent = document.getElementById('keepmebot');
  return (
    (agent?.classList.contains('opened') &&
      agent?.classList.contains('toggle')) ??
    false
  );
};

const removeAgentIframe = () => {
  const frameDom = document.getElementById('keepme');
  if (frameDom) frameDom.remove();

  const agent = document.getElementById('keepmebot');
  if (agent) agent.remove();

  const agentButton = document.getElementById('round-btn');
  if (agentButton) agentButton.remove();

  const scriptDom = document.getElementById('embedScript');
  if (scriptDom) scriptDom.remove();
};

const checkAuthentication = () => {
  return Boolean(localStorage.getItem('role'));
};

const getBookingType = (id: string, type: string) => {
  if (id.toLocaleLowerCase() === 'no booking') return 'No Booking';
  if (id.includes('_')) {
    const type = id.split('_')[0];
    console.log(type);
    if (type == 'feebBack') {
      return 'feedback';
    }
    return camelToTitleCase(type);
  }
  if (type.toLocaleLowerCase() == 'lead') return 'welcome lead';
  return 'confirmation';
};

const updateProfilePic = (profilePic: string | null) => {
  if (profilePic === null) {
    localStorage.removeItem('profilePic');
  } else {
    localStorage.setItem(
      'profilePic',
      encryptDecrypt.encrypt(profilePic) as string
    );
  }
};

const titleCaseToSpace = (text: string) => {
  return text
    .split(/(?=[A-Z])/)
    .join(' ')
    .trim();
};

let _store: any = null;

export const injectStore = (s: any) => {
  _store = s;
};

const getClientId = (): string | null => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];

  if (firstSegment && !isNonClientRoute('/' + firstSegment)) {
    const decrypted = encryptDecrypt.decryptClientId(firstSegment);
    if (decrypted) return decrypted;
  }

  return _store?.getState().activeClient?.id ?? getLastClientId();
};

export const getEncryptedClientId = (): string | null => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];

  if (firstSegment && !isNonClientRoute('/' + firstSegment)) {
    return firstSegment; // raw encrypted from URL
  }

  const realId = _store?.getState().activeClient?.id ?? getLastClientId();
  if (!realId) return null;
  return encryptDecrypt.encryptClientId(realId);
};

const getClient = (): string | null =>
  _store?.getState().activeClient?.name ?? null;

const getProfilePic = (): string | null =>
  _store?.getState().activeClient?.profile ?? null;

const getSalesReport = (): boolean | null =>
  _store?.getState().activeClient?.salesReport ?? null;

const checkReEngageEnabled = (): boolean => {
  return _store?.getState().activeClient?.reEngage === true;
};

const getFbRegionFromStorage = () => {
  return encryptDecrypt.decrypt(localStorage.getItem('fbRegion') as string);
};

const getFbBranchFromStorage = () => {
  return encryptDecrypt.decrypt(localStorage.getItem('fbBranch') as string);
};

const getRoleFromLocalStorage = () => {
  return encryptDecrypt.decrypt(localStorage.getItem('role') as string);
};

const numberWithCommas = (x: string | number) => {
  if (!x) {
    return '0';
  }
  return x.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const getVisitorName = (visitor: VisitorDetailsType) => {
  const firstName = visitor.details?.first_name || '';
  const lastName =
    visitor.details?.last_name?.toLowerCase() === 'n/a'
      ? ''
      : visitor.details?.last_name;
  const name = visitor.details?.name || '';
  const sender = visitor.sender || '';

  const fullName =
    firstName || lastName
      ? `${firstName} ${lastName}`.toLowerCase()
      : name
        ? name.toLowerCase()
        : sender.toLowerCase();

  if (visitor?.branch) {
    return `${fullName} - ${visitor.branch}`.trim();
  }

  return fullName;
};

const formatSeconds = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const getLastSeenDate = (conversation: any) => {
  const latestDate = Math.max(
    ...conversation.map((msg: any) =>
      msg?.updated_date ? new Date(msg?.updated_date)?.getTime() : 0
    )
  );

  // Format the last updated date
  const formattedDate = formatDate(latestDate, DATETIME_FORMAT);
  return formattedDate;
};

const getLastMessageDate = (
  visitor: VisitorDetailsType,
  timezone: string | null | undefined
) => {
  const agents = visitor?.agent || [];

  let latestCreatedDate = '';
  let latestAiMessage = '';

  if (visitor?.lastHistory && visitor.lastHistory?.length > 0) {
    for (const history of visitor.lastHistory) {
      if (
        !latestCreatedDate ||
        new Date(history.created_date) > new Date(latestCreatedDate)
      ) {
        latestCreatedDate = history.created_date;
        latestAiMessage = (
          history.ai_message ||
          history.human_message ||
          ''
        ).replace(/<br\s*\/?>/gi, '');
      }
    }
    return [
      latestAiMessage,
      formatDateWithTimezone(latestCreatedDate, DATETIME_FORMAT, timezone),
    ];
  }

  for (const agentType of agents) {
    const conversation = visitor[agentType];

    if (conversation && conversation.length > 0) {
      const latestAgentMessage = conversation[conversation.length - 1];

      if (
        !latestCreatedDate ||
        new Date(latestAgentMessage.created_date) > new Date(latestCreatedDate)
      ) {
        latestCreatedDate =
          latestAgentMessage.created_date || latestCreatedDate;
        latestAiMessage = (
          latestAiMessage ||
          latestAgentMessage.ai_message ||
          ''
        ).replace(/<br\s*\/?>/gi, '');
      }
    }
  }

  if (!latestCreatedDate) {
    return ['', ''];
  }

  return [
    latestAiMessage,
    formatDateWithTimezone(latestCreatedDate, DATETIME_FORMAT, timezone),
  ];
};

const getLastSeenUserDate = (conversation: any) => {
  const filteredMessages = conversation.filter(
    (msg: any) => msg?.human_message
  );

  const latestDate = Math.max(
    0,
    ...filteredMessages.map((msg: any) =>
      msg.updated_date ? new Date(msg.updated_date).getTime() : 0
    )
  );

  if (!latestDate || latestDate === 0) {
    return '';
  }

  const formattedDate =
    latestDate > 0 ? formatDate(latestDate, DATETIME_FORMAT) : '';
  return formattedDate;
};

function formatTime(time: any) {
  let [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes} ${period}`;
}

const dateInitialize = () => {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), 0, 2);
  return { startDate, endDate: today };
};

const oneMonthInitialize = () => {
  const today = new Date();
  const startDate = subDays(today, 30);
  return { startDate, endDate: today };
};

const formatDateRange = (startDate: Date, endDate: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  const start = startDate
    .toLocaleDateString('en-GB', options)
    .replace('Sept', 'Sep');
  const end = endDate
    .toLocaleDateString('en-GB', options)
    .replace('Sept', 'Sep');

  return `${start} - ${end}`;
};

const formatDateRangeNumber = (startDate: Date, endDate: Date) => {
  const formatToYYYYMMDD = (date: Date) => date.toISOString().split('T')[0];
  const start = formatToYYYYMMDD(startDate);
  const end = formatToYYYYMMDD(endDate);

  return `${start} - ${end}`;
};

const getFilterDate = () => {
  const { startDate, endDate } = dateInitialize();
  const initialDate = formatDateRangeNumber(startDate, endDate);
  return initialDate;
};

const getOneMonthFilterDate = () => {
  const { startDate, endDate } = oneMonthInitialize();
  const initialDate = formatDateRangeNumber(startDate, endDate);
  return initialDate;
};

const cutText = (text: string, length: number) => {
  if (text.split(' ').length > 1) {
    const string = text.substring(0, length);
    const splitText = string.split(' ');
    splitText.pop();
    return splitText.join(' ') + '...';
  } else {
    return text;
  }
};

const truncateText = (text: string, length: number) => {
  if (!text) return '';
  if (text.trim().length > length) {
    return text.trim().substring(0, length) + '...';
  } else {
    return text.trim();
  }
};

const formatDateTime = (isoString?: string) => {
  if (!isoString) return '';
  return dayjs(isoString).format('YYYY-MM-DDTHH:mm');
};

const formatDate = (
  date: string | number | undefined | Date,
  format: string
) => {
  if (!date) return 'N/A';
  return dayjs(date).format(format);
};

const formatDateUTC = (
  date: string | number | undefined | Date,
  format: string
) => {
  if (!date) return 'N/A';
  return dayjs.utc(date).format(format);
};

const formatDateWithTimezone = (
  date: string | number,
  format: string,
  timezone: string | undefined | null
) => {
  if (timezone) {
    const originalDate = moment(date);
    const adjustedDate = originalDate.tz(timezone);
    return adjustedDate.format(format.replace(':ss', '')); // Removing seconds
  }

  return dayjs(date).format(format.replace(':ss', '')); // Removing seconds
};

const capitalizeFirstLetter = (string: string) => {
  if (string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  } else {
    return '';
  }
};

const loginSuccess = (role: ROLE, organization_id?: string) => {
  const storage = localStorage;
  storage.setItem('role', encryptDecrypt.encrypt(role) as string);
  if (organization_id)
    storage.setItem(
      'client',
      encryptDecrypt.encrypt(organization_id) as string
    );
};

const onlyNumber = (string: string) => {
  if (string) {
    return string.replace(/\D/g, '');
  } else {
    return '';
  }
};

const formatCurrency = (number: number) => {
  if (number) {
    const formattedNumber = number?.toString().replace(/\D/g, '');
    const rest = formattedNumber.length % 3;
    let currency = formattedNumber.substr(0, rest);
    const thousand = formattedNumber.substr(rest).match(/\d{3}/g);
    let separator;

    if (thousand) {
      separator = rest ? ',' : '';
      currency += separator + thousand.join(',');
    }

    return currency;
  } else {
    return '';
  }
};

const timeAgo = (time: string) => {
  const date = new Date((time || '').replace(/-/g, '/').replace(/[TZ]/g, ' '));
  const diff = (new Date().getTime() - date.getTime()) / 1000;
  const dayDiff = Math.floor(diff / 86400);

  if (isNaN(dayDiff) || dayDiff < 0 || dayDiff >= 31) {
    return dayjs(time).format('MMMM DD, YYYY');
  }

  return (
    (dayDiff === 0 &&
      ((diff < 60 && 'just now') ||
        (diff < 120 && '1 minute ago') ||
        (diff < 3600 && Math.floor(diff / 60) + ' minutes ago') ||
        (diff < 7200 && '1 hour ago') ||
        (diff < 86400 && Math.floor(diff / 3600) + ' hours ago'))) ||
    (dayDiff === 1 && 'Yesterday') ||
    (dayDiff < 7 && dayDiff + ' days ago') ||
    (dayDiff < 31 && Math.ceil(dayDiff / 7) + ' weeks ago')
  );
};

const diffTimeByNow = (time: string) => {
  const startDate = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss').toString());
  const endDate = dayjs(dayjs(time).format('YYYY-MM-DD HH:mm:ss').toString());

  const duration = dayjs.duration(endDate.diff(startDate));
  const milliseconds = Math.floor(duration.asMilliseconds());

  const days = Math.round(milliseconds / 86400000);
  const hours = Math.round((milliseconds % 86400000) / 3600000);
  let minutes = Math.round(((milliseconds % 86400000) % 3600000) / 60000);
  const seconds = Math.round(
    (((milliseconds % 86400000) % 3600000) % 60000) / 1000
  );

  if (seconds < 30 && seconds >= 0) {
    minutes += 1;
  }

  return {
    days: days.toString().length < 2 ? '0' + days : days,
    hours: hours.toString().length < 2 ? '0' + hours : hours,
    minutes: minutes.toString().length < 2 ? '0' + minutes : minutes,
    seconds: seconds.toString().length < 2 ? '0' + seconds : seconds,
  };
};

const isset = (obj: object | string) => {
  if (obj !== null && obj !== undefined) {
    if (typeof obj === 'object' || Array.isArray(obj)) {
      return Object.keys(obj).length;
    } else {
      return obj.toString().length;
    }
  }

  return false;
};

const toRaw = (obj: object) => {
  return JSON.parse(JSON.stringify(obj));
};

const randomNumbers = (from: number, to: number, length: number) => {
  const numbers = [0];
  for (let i = 1; i < length; i++) {
    numbers.push(Math.ceil(Math.random() * (from - to) + to));
  }

  return numbers;
};

const toRGB = (value: string) => {
  return parseColor(value).color.join(' ');
};

const stringToHTML = (arg: string) => {
  const parser = new DOMParser(),
    DOM = parser.parseFromString(arg, 'text/html');
  return DOM.body.childNodes[0] as HTMLElement;
};

const camelToTitleCase = (str: string) => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, function (match) {
      return match.toUpperCase();
    })
    .trim();
};

const toTitleCase = (str: string) => {
  return str
    .replace(/[_\-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const slideUp = (
  el: HTMLElement,
  duration = 300,
  callback = (el: HTMLElement) => {}
) => {
  el.style.transitionProperty = 'height, margin, padding';
  el.style.transitionDuration = duration + 'ms';
  el.style.height = el.offsetHeight + 'px';
  el.offsetHeight;
  el.style.overflow = 'hidden';
  el.style.height = '0';
  el.style.paddingTop = '0';
  el.style.paddingBottom = '0';
  el.style.marginTop = '0';
  el.style.marginBottom = '0';
  window.setTimeout(() => {
    el.style.display = 'none';
    el.style.removeProperty('height');
    el.style.removeProperty('padding-top');
    el.style.removeProperty('padding-bottom');
    el.style.removeProperty('margin-top');
    el.style.removeProperty('margin-bottom');
    el.style.removeProperty('overflow');
    el.style.removeProperty('transition-duration');
    el.style.removeProperty('transition-property');
    callback(el);
  }, duration);
};

const slideDown = (
  el: HTMLElement,
  duration = 300,
  callback = (el: HTMLElement) => {}
) => {
  el.style.removeProperty('display');
  let display = window.getComputedStyle(el).display;
  if (display === 'none') display = 'block';
  el.style.display = display;
  let height = el.offsetHeight;
  el.style.overflow = 'hidden';
  el.style.height = '0';
  el.style.paddingTop = '0';
  el.style.paddingBottom = '0';
  el.style.marginTop = '0';
  el.style.marginBottom = '0';
  el.offsetHeight;
  el.style.transitionProperty = 'height, margin, padding';
  el.style.transitionDuration = duration + 'ms';
  el.style.height = height + 'px';
  el.style.removeProperty('padding-top');
  el.style.removeProperty('padding-bottom');
  el.style.removeProperty('margin-top');
  el.style.removeProperty('margin-bottom');
  window.setTimeout(() => {
    el.style.removeProperty('height');
    el.style.removeProperty('overflow');
    el.style.removeProperty('transition-duration');
    el.style.removeProperty('transition-property');
    callback(el);
  }, duration);
};

const getFormattedTime = (timestamp: string | number): string => {
  const date = new Date(Number(timestamp));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const now = new Date();
const pad = (num: number) => String(num).padStart(2, '0');

const extractVariables = (prompt: string): string[] => {
  const matches = prompt.match(/(?<!\{)\{([^{}]+)\}(?!\})/g) || [];
  return matches.map((v) => v.slice(1, -1).trim());
};

const getLocationInvalidVariables = (
  prompt: string,
  type: IPROMPT
): string[] => {
  const promptMap = new Map<IPROMPT, Set<string>>([
    ['prompt', ALLOWED_PROMPT_VARIABLES],
    ['ragPrompt', ALLOWED_LOCATION_RAG_PROMPT_VARIABLES],
    ['voicePrompt', ALLOWED_VOICE_PROMPT_VARIABLES],
  ]);
  const allowedVariables = promptMap.get(type);
  if (!allowedVariables) return [];
  return extractVariables(prompt).filter((key) => !allowedVariables.has(key));
};

const getRegionInvalidVariables = (prompt: string, type: IPROMPT): string[] => {
  const promptMap = new Map<IPROMPT, Set<string>>([
    ['prompt', ALLOWED_PROMPT_VARIABLES],
    ['ragPrompt', ALLOWED_REGION_RAG_PROMPT_VARIABLES],
    ['voicePrompt', ALLOWED_VOICE_PROMPT_VARIABLES],
  ]);
  const allowedVariables = promptMap.get(type);
  if (!allowedVariables) return [];
  return extractVariables(prompt).filter((key) => !allowedVariables.has(key));
};

const isLegacyWhatsappTemplate = (channels: string[], template?: Template) => {
  // * consider a template legacy if it does not have a sid and it's channel is Whatsapp
  // * legacy template -> templates created before the integration of Twilio API
  if (template?.sid) {
    return false;
  }
  if (channels.includes('WhatsApp')) {
    return true;
  }
  return false;
};

const normalizePhoneNumber = (value: string): string => {
  const trimmed = (value?.toString() ?? '').replace(/\s+/g, '');
  if (/^-?\d+(\.\d+)?e[+-]?\d+$/i.test(trimmed)) {
    const num = Number(trimmed);
    if (Number.isFinite(num)) {
      return num.toFixed(0);
    }
  }
  return trimmed;
};

const getPreviousDateRange = (range?: string | null): string | undefined => {
  if (!range) return undefined;

  const parts = range.split(' - ').map((p) => p.trim());
  if (parts.length !== 2) return undefined;

  const [startStr, endStr] = parts;
  const start = new Date(startStr);
  const end = new Date(endStr);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return undefined;

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  // inclusive day count of the currently selected range
  const durationDays =
    Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;

  const prevEnd = new Date(start.getTime() - MS_PER_DAY);
  const prevStart = new Date(
    prevEnd.getTime() - (durationDays - 1) * MS_PER_DAY
  );

  const fmt = (d: Date) => d.toISOString().slice(0, 10); // YYYY-MM-DD

  return `${fmt(prevStart)} - ${fmt(prevEnd)}`;
};

const calcGrowth = (
  current?: number | null,
  previous?: number | null
): number | undefined => {
  if (current === undefined || current === null) return undefined;
  if (previous === undefined || previous === null) return undefined;

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return parseFloat((((current - previous) / previous) * 100).toFixed(1));
};

// A page admin replying from Business Suite bypasses the agent; those History
// docs carry role 'admin'. The customer's own messages during a takeover share
// agentType 'takeover' but have role null, so role is the only reliable signal.
const isAdminReply = (message?: { role?: string | null }) =>
  message?.role === 'admin';

export {
  camelToTitleCase,
  capitalizeFirstLetter,
  checkAuthentication,
  cutText,
  dateInitialize,
  diffTimeByNow,
  downloadCSV,
  downloadFile,
  findSessionDuration,
  formatCurrency,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatDateUTC,
  formatDateWithTimezone,
  formatSeconds,
  formatTime,
  getBookingType,
  getClient,
  getClientId,
  getFbBranchFromStorage,
  getFbRegionFromStorage,
  getFilterDate,
  getFormattedTime,
  getLastMessageDate,
  getLastSeenDate,
  getLastSeenUserDate,
  getLocationInvalidVariables,
  isAdminReply,
  getOneMonthFilterDate,
  getProfilePic,
  getRegionInvalidVariables,
  getRoleFromLocalStorage,
  getSalesReport,
  getVisitorName,
  isLegacyWhatsappTemplate,
  isset,
  loginSuccess,
  normalizePhoneNumber,
  now,
  checkReEngageEnabled,
  numberWithCommas,
  oneMonthInitialize,
  onlyNumber,
  pad,
  randomNumbers,
  removeAgentIframe,
  isAgentWidgetOpen,
  slideDown,
  slideUp,
  stringToHTML,
  timeAgo,
  titleCaseToSpace,
  toRaw,
  toRGB,
  toTitleCase,
  truncateText,
  updateProfilePic,
  getPreviousDateRange,
  calcGrowth,
};
